/**
 * useAssignments Hook
 * Manages project-template assignments via REST API.
 * Provides atomic create+assign operations and reverse lookups.
 */

import { useState, useCallback } from 'react';
import { useAuthContext } from '@/contexts/AuthContext';
import { projectsApi } from '@/services/projectsApi';
import type { TemplateMetadata, SaveTemplateRequest } from '@/services/templatesApi';
import { createLogger } from '@/lib/logger';

const log = createLogger('useAssignments');

/** Assignment item (simplified for API-based approach) */
export interface AssignmentItem {
  projectId: string;
  templateId: string;
}

export interface UseAssignmentsReturn {
  /** Get all template IDs assigned to a project */
  getProjectTemplateIds: (projectId: string) => Promise<string[]>;

  /** Get full assignment items for a project */
  getProjectAssignments: (projectId: string) => Promise<AssignmentItem[]>;

  /** Get all templates assigned to a project (fetches template metadata) */
  getProjectTemplates: (projectId: string) => Promise<TemplateMetadata[]>;

  /** Assign an existing template to a project */
  assignTemplate: (projectId: string, templateId: string) => Promise<void>;

  /** Unassign a template from a project */
  unassignTemplate: (projectId: string, templateId: string) => Promise<void>;

  /** Create a new template and assign it to a project */
  createAndAssign: (projectId: string, request: SaveTemplateRequest) => Promise<TemplateMetadata>;

  /** Get all projects that use a specific template (reverse lookup) */
  getTemplateProjects: (templateId: string) => Promise<AssignmentItem[]>;

  /** Loading state */
  isLoading: boolean;
}

export function useAssignments(): UseAssignmentsReturn {
  const { isAuthenticated } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);

  const ensureConfigured = () => {
    if (!isAuthenticated) {
      throw new Error('User not authenticated');
    }
  };

  /** Get the current project's templateIds from the API */
  const getProjectTemplateIdsFromApi = async (projectId: string): Promise<string[]> => {
    const templates = await projectsApi.getProjectTemplates(projectId);
    return templates.map(t => t.id);
  };

  const handleGetProjectTemplateIds = useCallback(async (projectId: string): Promise<string[]> => {
    ensureConfigured();
    return getProjectTemplateIdsFromApi(projectId);
  }, [isAuthenticated]);

  const handleGetProjectAssignments = useCallback(async (projectId: string): Promise<AssignmentItem[]> => {
    ensureConfigured();
    const templateIds = await getProjectTemplateIdsFromApi(projectId);
    return templateIds.map(templateId => ({ projectId, templateId }));
  }, [isAuthenticated]);

  const handleGetProjectTemplates = useCallback(async (projectId: string): Promise<TemplateMetadata[]> => {
    ensureConfigured();
    return projectsApi.getProjectTemplates(projectId);
  }, [isAuthenticated]);

  const handleAssignTemplate = useCallback(async (projectId: string, templateId: string): Promise<void> => {
    ensureConfigured();
    setIsLoading(true);
    try {
      await projectsApi.assignTemplate(projectId, { templateId });
      log.info('Template assigned to project', { projectId, templateId });
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  const handleUnassignTemplate = useCallback(async (projectId: string, templateId: string): Promise<void> => {
    ensureConfigured();
    setIsLoading(true);
    try {
      // Get current templateIds and remove the target
      const currentIds = await getProjectTemplateIdsFromApi(projectId);
      const filtered = currentIds.filter(id => id !== templateId);
      if (filtered.length === currentIds.length) return;

      await projectsApi.updateProject(projectId, {
        templateIds: filtered,
      });
      log.info('Template unassigned from project', { projectId, templateId });
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  const handleCreateAndAssign = useCallback(async (
    projectId: string,
    request: SaveTemplateRequest
  ): Promise<TemplateMetadata> => {
    ensureConfigured();
    setIsLoading(true);
    try {
      const result = await projectsApi.assignTemplate(projectId, { template: request });
      const tpl = result.template;
      log.info('Template created and assigned', { projectId, templateId: tpl.id });
      return {
        id: tpl.id!,
        name: tpl.name!,
        description: tpl.description || '',
        outputFormat: tpl.outputFormat || 'custom',
        icon: tpl.icon,
        color: tpl.color,
        createdAt: tpl.createdAt || new Date().toISOString(),
        lastUpdated: tpl.lastUpdated || new Date().toISOString(),
        createdBy: '',
      } as TemplateMetadata;
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  const handleGetTemplateProjects = useCallback(async (templateId: string): Promise<AssignmentItem[]> => {
    ensureConfigured();
    // Server-side filter: only returns projects that contain this templateId
    const projects = await projectsApi.listProjects(false, templateId);
    return projects.map(p => ({ projectId: p.id, templateId }));
  }, [isAuthenticated]);

  return {
    getProjectTemplateIds: handleGetProjectTemplateIds,
    getProjectAssignments: handleGetProjectAssignments,
    getProjectTemplates: handleGetProjectTemplates,
    assignTemplate: handleAssignTemplate,
    unassignTemplate: handleUnassignTemplate,
    createAndAssign: handleCreateAndAssign,
    getTemplateProjects: handleGetTemplateProjects,
    isLoading,
  };
}
